import React, { useState, useEffect } from 'react';
import '../../../App.scss';
import 'antd/dist/antd.css';
import { PageHeader, Table, Button, Space, DatePicker, Tag, Avatar } from 'antd';
import { PictureOutlined, TeamOutlined, CalendarOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useClaimedTags } from '../../../models/useClaimedTags';
import { useClientDisplayNames } from '../../../models/useClientDisplayNames';
import { useClaimerRoles } from '../../../models/useClaimerRoles';
import { ClientsSearch } from '../../../components/admin/AdminClientsComponents';
import { templateTagIdOf } from '../../../utils/tagTemplate';
import CenteredSpinner from '../../../components/CenteredSpinner';
import { getAdminDatePresets, getCurrentWeekRange } from '../../../utils/adminDatePresets';
import { escapeCsvValue, toIsoString, getIsAutoClaimed } from '../../../utils/adminTagsClaimedUtils';
import { getAppBaseUrl } from '../../../utils/URLBuilder';

const { RangePicker } = DatePicker;
const LIMIT = 400;

const URLPath = getAppBaseUrl();

const STATUS_COLORS = { live: 'green', paused: 'orange', archived: 'default' };
const ROLE_COLORS = { owner: 'red', admin: 'gold', member: 'blue', manager: 'purple' };

export default function AdminTagsClaimedScreen({ locStrings, isAdmin }) {
    const [dateRange, setDateRange] = useState(getCurrentWeekRange);
    const [, setPresetRefresh] = useState(0);
    const startDate = dateRange && dateRange[0] ? dateRange[0].toDate() : null;
    const endDate = dateRange && dateRange[1] ? dateRange[1].toDate() : null;

    const [loading, tags] = useClaimedTags({ startDate, endDate, limit: LIMIT });
    const [searchText, setSearchText] = useState('');
    const [filteredTags, setFilteredTags] = useState(null);

    const clientIdsToResolve = React.useMemo(() => {
        const list = tags || [];
        const ids = new Set();
        list.forEach((t) => {
            if (!t.teamId && t.clientId) ids.add(t.clientId);
            if (t.claimedBy) ids.add(t.claimedBy);
            if (t.teamId && t.teamOwnerClientId) ids.add(t.teamOwnerClientId);
        });
        return [...ids];
    }, [tags]);
    const clientDisplayNames = useClientDisplayNames(clientIdsToResolve);

    const claimerRolePairs = React.useMemo(() => {
        const list = tags || [];
        return list
            .filter((t) => t.teamId && (t.claimedBy || t.teamOwnerClientId))
            .map((t) => ({
                teamId: t.teamId,
                clientId: t.claimedBy || t.teamOwnerClientId,
            }));
    }, [tags]);
    const claimerRolesMap = useClaimerRoles(claimerRolePairs);

    useEffect(() => {
        const words = (searchText || '').toLowerCase().trim().split(/\s+/).filter(Boolean);
        if (words.length === 0) {
            setFilteredTags(null);
            return;
        }
        const filtered = (tags || []).filter((tag) => {
            const templateTagId = templateTagIdOf(tag);
            const typeStr = tag.teamId ? 'team' : 'individual';
            const ownerDisplayName = tag.teamId
                ? (tag.teamName || tag.teamId)
                : (tag.displayName || clientDisplayNames[tag.clientId] || tag.nameOrDesc || tag.clientId);
            const isAutoClaimed = getIsAutoClaimed(tag);
            const effectiveClaimerId = tag.claimedBy || (tag.teamId ? tag.teamOwnerClientId : tag.clientId);
            const claimerDisplayName = effectiveClaimerId
                ? (clientDisplayNames[effectiveClaimerId] || effectiveClaimerId)
                : null;
            const haystack = [
                tag.id,
                templateTagId,
                tag.clientId,
                tag.teamId,
                tag.teamName,
                tag.teamOwnerClientId,
                tag.claimedBy,
                tag.status,
                typeStr,
                ownerDisplayName,
                claimerDisplayName,
                isAutoClaimed ? 'auto' : null,
            ].filter(Boolean).join(' ').toLowerCase();
            return words.every((w) => haystack.includes(w));
        });
        setFilteredTags(filtered);
    }, [searchText, tags, clientDisplayNames]);

    const onSearch = (value) => setSearchText(value || '');
    const onDateRangeChange = (range) => setDateRange(range && range[0] && range[1] ? range : getCurrentWeekRange());

    const getTemplateTagId = templateTagIdOf;

    const exportCsv = () => {
        const exportList = filteredTags || tags || [];
        const getOwnerDisplayName = (tag) => {
            if (tag.teamId) return tag.teamName || tag.teamId;
            return tag.displayName || clientDisplayNames[tag.clientId] || tag.nameOrDesc || tag.clientId;
        };
        const getEffectiveClaimer = (tag) => {
            const isAutoClaimed = getIsAutoClaimed(tag);
            const claimerId = tag.claimedBy || (tag.teamId ? tag.teamOwnerClientId : tag.clientId);
            const claimerDisplayName = claimerId ? (clientDisplayNames[claimerId] || claimerId) : '';
            const roleKey = tag.teamId && claimerId ? `${tag.teamId}-${claimerId}` : null;
            const roles = roleKey ? (claimerRolesMap[roleKey] || []) : [];
            const primaryRole = roles.length > 0 ? roles[0] : (isAutoClaimed && tag.teamId ? 'owner' : '');
            return { claimerId, claimerDisplayName, primaryRole, isAutoClaimed };
        };
        const rows = [
            ['tagId', 'templateTagId', 'clientId', 'ownerDisplayName', 'type', 'teamId', 'teamName', 'teamOwnerClientId', 'teamTapBehaviour', 'claimedBy', 'claimedByDisplayName', 'claimedByRole', 'isAutoClaimed', 'status', 'createdAt', 'updatedAt', 'nameOrDesc', 'topLogo', 'avatar'],
            ...exportList.map((tag) => {
                const { claimerId, claimerDisplayName, primaryRole, isAutoClaimed } = getEffectiveClaimer(tag);
                return [
                    tag.id || '',
                    getTemplateTagId(tag) || '',
                    tag.clientId || '',
                    getOwnerDisplayName(tag),
                    tag.teamId ? 'team' : 'individual',
                    tag.teamId || '',
                    tag.teamName || '',
                    tag.teamOwnerClientId || '',
                    tag.teamTapBehaviour || '',
                    claimerId || '',
                    claimerDisplayName,
                    primaryRole,
                    isAutoClaimed,
                    tag.status || '',
                    toIsoString(tag.createdAt),
                    toIsoString(tag.updatedAt),
                    tag.nameOrDesc || '',
                    tag.topLogo || '',
                    tag.avatar || '',
                ];
            }),
        ];
        const csv = rows.map((row) => row.map(escapeCsvValue).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `admin-tags-claimed-${moment().format('YYYYMMDD-HHmmss')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const displayList = filteredTags || tags || [];
    const hasRows = displayList.length > 0;
    const resultCount = displayList.length;
    const atLimit = resultCount >= LIMIT;

    const columns = [
        {
            title: 'Owner',
            key: 'owner',
            render: (_, tag) => {
                const isTeamTag = !!tag.teamId;
                const imgSrc = tag.topLogo || tag.avatar;
                const teamPageUrl = tag.teamId && tag.teamOwnerClientId
                    ? `${URLPath}/admin/clients/${tag.teamOwnerClientId}/teams/${tag.teamId}`
                    : null;
                const clientPageUrl = `${URLPath}/admin/clients/${tag.clientId}`;
                const displayName = isTeamTag
                    ? (tag.teamName || tag.teamId)
                    : (tag.displayName || clientDisplayNames[tag.clientId] || tag.nameOrDesc || tag.clientId);
                const href = isTeamTag ? teamPageUrl : clientPageUrl;
                const claimedLabel = tag.createdAt
                    ? moment(tag.createdAt.toMillis ? tag.createdAt.toMillis() : tag.createdAt).format('YYYY-MM-DD HH:mm')
                    : '-';
                return (
                    <Space direction="vertical" size={2}>
                        <Space wrap>
                            <Avatar
                                shape={isTeamTag ? 'square' : 'circle'}
                                src={imgSrc}
                                icon={isTeamTag ? <TeamOutlined /> : <PictureOutlined />}
                                size={32}
                                style={isTeamTag ? { borderRadius: 6, flexShrink: 0 } : { flexShrink: 0 }}
                            />
                            {href ? (
                                <a href={href}>{displayName}</a>
                            ) : (
                                <span>{displayName}</span>
                            )}
                            <Tag color={isTeamTag ? 'blue' : 'green'}>{isTeamTag ? 'Team' : 'Individual'}</Tag>
                        </Space>
                        <Space size={6} style={{ fontSize: 12 }}>
                            <CalendarOutlined />
                            <span>{claimedLabel}</span>
                        </Space>
                    </Space>
                );
            },
        },
        {
            title: 'Claimed by',
            key: 'claimedBy',
            render: (_, tag) => {
                const isTeamTag = !!tag.teamId;
                const isAutoClaimed = getIsAutoClaimed(tag);
                const claimerId = tag.claimedBy || (isTeamTag ? tag.teamOwnerClientId : tag.clientId);
                if (!claimerId) return <span style={{ color: '#999' }}>—</span>;
                const claimerHref = `${URLPath}/admin/clients/${claimerId}`;
                const claimerName = clientDisplayNames[claimerId] || claimerId;
                const roleKey = isTeamTag ? `${tag.teamId}-${claimerId}` : null;
                const roles = roleKey ? (claimerRolesMap[roleKey] || []) : [];
                const primaryRole = roles.length > 0 ? roles[0] : (isAutoClaimed && isTeamTag ? 'owner' : null);
                return (
                    <Space direction="vertical" size={2}>
                        <Space wrap>
                            <Avatar
                                size={24}
                                icon={<PictureOutlined />}
                                style={{ flexShrink: 0 }}
                            />
                            <a href={claimerHref}>{claimerName}</a>
                            {isAutoClaimed && <Tag color="default">auto</Tag>}
                            {primaryRole && (
                                <Tag color={ROLE_COLORS[primaryRole?.toLowerCase()] || 'default'}>{primaryRole}</Tag>
                            )}
                        </Space>
                    </Space>
                );
            },
        },
        {
            title: 'Tag',
            key: 'tag',
            render: (_, tag) => {
                const tagLink = tag.teamId && tag.teamOwnerClientId
                    ? `${URLPath}/admin/clients/${tag.teamOwnerClientId}/teams/${tag.teamId}/tags/${tag.id}`
                    : `${URLPath}/admin/clients/${tag.clientId}/tags/${tag.id}`;
                const tplId = getTemplateTagId(tag);
                const statusTag = tag.status ? (
                    <Tag color={STATUS_COLORS[tag.status?.toLowerCase()] || 'default'}>{tag.status}</Tag>
                ) : null;
                return (
                    <Space direction="vertical" size={2}>
                        <a href={tagLink}>{tag.id}</a>
                        <Space size={4}>
                            {tplId && <Tag color="default" style={{ marginRight: 0 }}>{tplId}</Tag>}
                            {statusTag}
                        </Space>
                    </Space>
                );
            },
        },
    ];

    if (!isAdmin) {
        return <div>You are not authorized to view this page.</div>;
    }

    return (
        <div style={{ height: '100%', textAlign: 'center' }}>
            <PageHeader ghost={false} title="Tags Claimed" />
            <Space direction="vertical" style={{ width: '100%', padding: 16 }}>
                <ClientsSearch initialValue={searchText} callbackOnSearch={onSearch} />
                <Space wrap>
                    <RangePicker
                        value={dateRange}
                        onChange={onDateRangeChange}
                        onOpenChange={(open) => { if (open) setPresetRefresh((t) => t + 1); }}
                        ranges={getAdminDatePresets()}
                        allowClear={false}
                    />
                    <span style={{ color: atLimit ? '#ff4d4f' : undefined }}>
                        {resultCount} result{resultCount !== 1 ? 's' : ''} (max {LIMIT})
                    </span>
                    <Button onClick={exportCsv} disabled={!hasRows}>Export CSV</Button>
                </Space>
                {loading ? (
                    <CenteredSpinner loading={true} />
                ) : (
                    <Table
                        dataSource={displayList}
                        columns={columns}
                        rowKey="id"
                        pagination={{ pageSize: 50 }}
                        scroll={{ x: 800 }}
                    />
                )}
            </Space>
        </div>
    );
}
